/**
 * Help beside the bell (owner, 2026-09-27: "link to help, faq is not in the app itself, it should be
 * maybe along notifications with a circle and question mark and popover"). The guides and the FAQ
 * live on timefly.no/hjelp, built from docs/user; they open in a new tab so the work stays open.
 */
import { T } from '@/i18n';
import { useWelcome } from '@/shell/Welcome';
import { useTenant } from '@/shell/tenant';
import { Icon } from '@/ui/icons';
import * as Popover from '@radix-ui/react-popover';
import { useLocation } from 'react-router-dom';

const HELP = 'https://timefly.no/hjelp/';

/** The manager guide for the page that is open (docs/user/leder, published at /hjelp/leder/). */
const GUIDES: [string, string, string][] = [
  ['/hjem', 'dagen', 'Oversikt og dagen'],
  ['/dagen', 'dagen', 'Oversikt og dagen'],
  ['/varsler', 'varsler-og-varslinger', 'Oppfølging og varsler'],
  ['/vaktplan', 'vaktplan', 'Vaktplan'],
  ['/timeliste', 'timeliste-og-godkjenning', 'Kontroll og godkjenning'],
  ['/arbeidstid', 'arbeidstidsoversikt', 'Registreringer og arbeidstidsoversikt'],
  ['/lonn', 'timer-til-lonn', 'Rapporter og lønn'],
  ['/omsetning', 'timer-til-lonn', 'Rapporter og lønn'],
  ['/deltid', 'deltid', 'Deltid'],
  ['/fravaer', 'fravaer', 'Fravær'],
  ['/ansatte', 'ansatte', 'Ansatte'],
  ['/innstillinger', 'innstillinger', 'Innstillinger'],
];
/** With planning off the week grid is «Uka» and has its own guide (#118). */
export const guideFor = (pathname: string, planning = true): [string, string, string] | null => {
  const g = GUIDES.find(([p]) => pathname === p || pathname.startsWith(`${p}/`)) ?? null;
  return g && g[0] === '/vaktplan' && !planning ? ['/vaktplan', 'uka', 'Uka'] : g;
};

export function HelpButton() {
  const { pathname } = useLocation();
  const { tenant, mode } = useTenant();
  const guide = guideFor(pathname, mode === 'rota');
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className="bell" aria-label={T('Hjelp')} title={T('Hjelp')}>
          <Icon name="help" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="pop helppop" align="end" sideOffset={6} aria-label={T('Hjelp')}>
          <h6>{T('Hjelp')}</h6>
          {/* the welcome overview again, for the administrator it is shown to (#130) */}
          {tenant?.me?.is_admin && (
            <Popover.Close asChild>
              <button type="button" data-testid="help-welcome" onClick={() => useWelcome.getState().show()}>
                <b>{T('Kom i gang')}</b>
                <span>{T('Velg hva du vil gjøre først')}</span>
              </button>
            </Popover.Close>
          )}
          {guide && (
            <a href={`${HELP}leder/${guide[1]}.html`} target="_blank" rel="noreferrer">
              <b>{T('Om denne siden: {page}', { page: T(guide[2]) })}</b>
              <span>{T('Veiledningen for siden du står på')}</span>
            </a>
          )}
          <a href={HELP} target="_blank" rel="noreferrer">
            <b>{T('Veiledninger')}</b>
            <span>{T('Slik bruker du vaktplan, stempling, fravær og lønn')}</span>
          </a>
          <a href={`${HELP}ofte-stilte-sporsmal.html`} target="_blank" rel="noreferrer">
            <b>{T('Ofte stilte spørsmål')}</b>
            <span>{T('Korte svar på det folk lurer på')}</span>
          </a>
          <a href={`${HELP}feilmeldinger.html`} target="_blank" rel="noreferrer">
            <b>{T('Feilmeldinger')}</b>
            <span>{T('Hva en melding betyr, og hva du gjør')}</span>
          </a>
          <a href="mailto:post@timefly.no">
            <b>{T('Kontakt oss')}</b>
            <span>post@timefly.no</span>
          </a>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
